import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import threadApi, { initializeThreadApi } from "../api/threadApi";

/**
 * Thread Context for managing thread state
 * Requirements: 2.3, 7.4, 8.3
 */

// Action types
const THREAD_ACTIONS = {
  // Thread loading actions
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",

  // Thread data actions
  SET_CURRENT_THREAD: "SET_CURRENT_THREAD",
  UPDATE_THREAD_METADATA: "UPDATE_THREAD_METADATA",
  CLEAR_CURRENT_THREAD: "CLEAR_CURRENT_THREAD",

  // Message actions (for future use)
  SET_MESSAGES: "SET_MESSAGES",
  ADD_MESSAGE: "ADD_MESSAGE",
  UPDATE_MESSAGE: "UPDATE_MESSAGE",
  REMOVE_MESSAGE: "REMOVE_MESSAGE",

  // Reply actions (for future use)
  SET_REPLIES: "SET_REPLIES",
  ADD_REPLY: "ADD_REPLY",
  UPDATE_REPLY: "UPDATE_REPLY",

  // Pagination actions
  SET_PAGINATION: "SET_PAGINATION",
  UPDATE_PAGINATION: "UPDATE_PAGINATION",

  // Real-time update actions
  THREAD_UPDATED: "THREAD_UPDATED",
  MESSAGE_COUNT_UPDATED: "MESSAGE_COUNT_UPDATED",
  PARTICIPANT_COUNT_UPDATED: "PARTICIPANT_COUNT_UPDATED",
};

// Initial state structure
const initialState = {
  // Current thread data
  currentThread: null,
  fight: null,
  event: null,
  metadata: null,

  // Messages and replies (for future use)
  messages: new Map(),
  replies: new Map(),

  // Loading states
  loading: {
    thread: false,
    messages: false,
    posting: false,
    liking: false,
  },

  // Error state
  error: null,

  // Pagination state
  pagination: {
    messages: {
      page: 1,
      hasMore: false,
      loading: false,
      total: 0,
    },
    replies: new Map(), // messageId -> pagination state
  },

  // Cache and performance
  cache: {
    threads: new Map(),
    lastAccessed: new Map(),
  },

  // Real-time state
  realTime: {
    connected: false,
    lastUpdate: null,
  },
};

// Thread reducer
const threadReducer = (state, action) => {
  switch (action.type) {
    case THREAD_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.type]: action.payload.loading,
        },
      };

    case THREAD_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: {
          ...state.loading,
          thread: false,
          messages: false,
          posting: false,
          liking: false,
        },
      };

    case THREAD_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case THREAD_ACTIONS.SET_CURRENT_THREAD:
      const { thread, fight, event, metadata } = action.payload;

      // Cache the thread data
      const newCache = new Map(state.cache.threads);
      if (thread?.id) {
        newCache.set(thread.id, { thread, fight, event, metadata });

        // Update last accessed
        const newLastAccessed = new Map(state.cache.lastAccessed);
        newLastAccessed.set(thread.id, Date.now());

        return {
          ...state,
          currentThread: thread,
          fight,
          event,
          metadata,
          error: null,
          loading: {
            ...state.loading,
            thread: false,
          },
          cache: {
            threads: newCache,
            lastAccessed: newLastAccessed,
          },
        };
      }

      return {
        ...state,
        currentThread: thread,
        fight,
        event,
        metadata,
        error: null,
        loading: {
          ...state.loading,
          thread: false,
        },
      };

    case THREAD_ACTIONS.UPDATE_THREAD_METADATA:
      if (!state.currentThread) return state;

      return {
        ...state,
        currentThread: {
          ...state.currentThread,
          ...action.payload,
        },
      };

    case THREAD_ACTIONS.CLEAR_CURRENT_THREAD:
      return {
        ...state,
        currentThread: null,
        fight: null,
        event: null,
        metadata: null,
        messages: new Map(),
        replies: new Map(),
        error: null,
      };

    case THREAD_ACTIONS.SET_MESSAGES:
      // Sort messages by createdAt (newest first) before storing
      const sortedMessagesFromAPI = action.payload.messages.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      return {
        ...state,
        messages: new Map(sortedMessagesFromAPI.map((msg) => [msg._id, msg])),
        loading: {
          ...state.loading,
          messages: false,
        },
      };

    case THREAD_ACTIONS.ADD_MESSAGE:
      const newMessages = new Map(state.messages);
      newMessages.set(action.payload._id, action.payload);

      // Convert to array, sort by createdAt (newest first), then back to Map
      const sortedMessages = Array.from(newMessages.values()).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      const sortedMessagesMap = new Map(
        sortedMessages.map((msg) => [msg._id, msg])
      );

      return {
        ...state,
        messages: sortedMessagesMap,
        currentThread: state.currentThread
          ? {
              ...state.currentThread,
              messageCount: (state.currentThread.messageCount || 0) + 1,
            }
          : null,
      };

    case THREAD_ACTIONS.UPDATE_MESSAGE:
      if (!state.messages.has(action.payload._id)) return state;

      const updatedMessages = new Map(state.messages);
      updatedMessages.set(action.payload._id, {
        ...updatedMessages.get(action.payload._id),
        ...action.payload,
      });

      return {
        ...state,
        messages: updatedMessages,
      };

    case THREAD_ACTIONS.SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.type]: action.payload.pagination,
        },
      };

    case THREAD_ACTIONS.UPDATE_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.type]: {
            ...state.pagination[action.payload.type],
            ...action.payload.updates,
          },
        },
      };

    case THREAD_ACTIONS.THREAD_UPDATED:
      return {
        ...state,
        realTime: {
          ...state.realTime,
          lastUpdate: Date.now(),
        },
      };

    case THREAD_ACTIONS.MESSAGE_COUNT_UPDATED:
      if (!state.currentThread) return state;

      return {
        ...state,
        currentThread: {
          ...state.currentThread,
          messageCount: action.payload,
        },
      };

    case THREAD_ACTIONS.PARTICIPANT_COUNT_UPDATED:
      if (!state.currentThread) return state;

      return {
        ...state,
        currentThread: {
          ...state.currentThread,
          participantCount: action.payload,
        },
      };

    default:
      return state;
  }
};

// Create context
const ThreadContext = createContext();

// Custom hook to use thread context
export const useThreadContext = () => {
  const context = useContext(ThreadContext);
  if (!context) {
    throw new Error("useThreadContext must be used within a ThreadProvider");
  }
  return context;
};

// Thread Provider component
export const ThreadProvider = ({ children }) => {
  const [state, dispatch] = useReducer(threadReducer, initialState);
  const { getAccessTokenSilently } = useAuth0();
  const initRef = useRef(false);

  // Initialize ThreadAPI
  React.useEffect(() => {
    if (getAccessTokenSilently && !initRef.current) {
      initializeThreadApi(getAccessTokenSilently);
      initRef.current = true;
    }
  }, [getAccessTokenSilently]);

  // Action creators
  const setLoading = useCallback((type, loading) => {
    dispatch({
      type: THREAD_ACTIONS.SET_LOADING,
      payload: { type, loading },
    });
  }, []);

  const setError = useCallback((error) => {
    dispatch({
      type: THREAD_ACTIONS.SET_ERROR,
      payload: error,
    });
  }, []);

  const clearError = useCallback(() => {
    dispatch({
      type: THREAD_ACTIONS.CLEAR_ERROR,
    });
  }, []);

  const setCurrentThread = useCallback((threadData) => {
    dispatch({
      type: THREAD_ACTIONS.SET_CURRENT_THREAD,
      payload: threadData,
    });
  }, []);

  const updateThreadMetadata = useCallback((updates) => {
    dispatch({
      type: THREAD_ACTIONS.UPDATE_THREAD_METADATA,
      payload: updates,
    });
  }, []);

  const clearCurrentThread = useCallback(() => {
    dispatch({
      type: THREAD_ACTIONS.CLEAR_CURRENT_THREAD,
    });
  }, []);

  // Thread operations
  const loadFightThread = useCallback(
    async (fightId) => {
      if (!fightId) {
        setError("Fight ID is required");
        return null;
      }

      // Ensure ThreadAPI is initialized before making requests
      if (getAccessTokenSilently && !initRef.current) {
        initializeThreadApi(getAccessTokenSilently);
        initRef.current = true;
      }

      setLoading("thread", true);
      clearError();

      try {
        const response = await threadApi.getFightThread(fightId);
        setCurrentThread(response);

        return response;
      } catch (error) {
        let errorMessage = "Failed to load thread";

        if (error.type === "AUTH_ERROR") {
          errorMessage = "Authentication required to view this thread";
        } else if (error.type === "NOT_FOUND_ERROR") {
          errorMessage = "Thread not found or no longer available";
        } else if (error.type === "NETWORK_ERROR") {
          errorMessage =
            "Connection error - please check your internet connection";
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);

        throw error;
      } finally {
        setLoading("thread", false);
      }
    },
    [setLoading, clearError, setCurrentThread, setError, getAccessTokenSilently]
  );

  // Message operations
  const postMessage = useCallback(
    async (messageData) => {
      const { threadId, content, parentMessageId } = messageData;

      if (!threadId) {
        throw new Error("Thread ID is required");
      }

      if (!content || !content.trim()) {
        throw new Error("Message content is required");
      }

      // Ensure ThreadAPI is initialized before making requests
      if (getAccessTokenSilently && !initRef.current) {
        initializeThreadApi(getAccessTokenSilently);
        initRef.current = true;
      }

      setLoading("posting", true);
      clearError();

      try {
        let response;

        if (parentMessageId) {
          // This is a reply - will be implemented in future tasks
          throw new Error("Reply functionality not yet implemented");
        } else {
          // This is a regular message
          response = await threadApi.postMessage(threadId, content);
        }

        // Add message to state with optimistic update
        dispatch({
          type: THREAD_ACTIONS.ADD_MESSAGE,
          payload: response,
        });

        return response;
      } catch (error) {
        let errorMessage = "Failed to post message";

        if (error.type === "AUTH_ERROR") {
          errorMessage = "Authentication required to post messages";
        } else if (error.type === "RATE_LIMIT_ERROR") {
          errorMessage =
            "You are posting too quickly - please wait before trying again";
        } else if (error.type === "NETWORK_ERROR") {
          errorMessage =
            "Connection error - please check your internet connection";
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);

        throw error;
      } finally {
        setLoading("posting", false);
      }
    },
    [setLoading, clearError, setError, getAccessTokenSilently]
  );

  // Load messages for current thread
  const loadMessages = useCallback(
    async (
      page = 1,
      limit = 10,
      threadIdOverride = null,
      bypassCache = false
    ) => {
      const targetThreadId = threadIdOverride || state.currentThread?.id;

      if (!targetThreadId) {
        throw new Error("No current thread to load messages for");
      }

      // Ensure ThreadAPI is initialized before making calls
      if (!getAccessTokenSilently) {
        throw new Error(
          "Authentication not available - ThreadAPI not initialized"
        );
      }

      // Initialize ThreadAPI if not already done
      if (!initRef.current) {
        initializeThreadApi(getAccessTokenSilently);
        initRef.current = true;
      }

      setLoading("messages", true);
      clearError();

      try {
        // Pass useCache parameter to bypass cache when refreshing
        console.log("📡 ThreadContext: Calling threadApi.getMessages with:", {
          threadId: targetThreadId,
          page,
          limit,
          useCache: !bypassCache,
        });

        const response = await threadApi.getMessages(
          targetThreadId,
          page,
          limit,
          !bypassCache // useCache = !bypassCache
        );

        console.log("📡 ThreadContext: Got response from API:", {
          messageCount: response?.messages?.length,
          pagination: response?.pagination,
          messages: response?.messages?.map((m) => ({
            id: m._id,
            content: m.content.substring(0, 30),
          })),
        });

        // Set messages in state
        dispatch({
          type: THREAD_ACTIONS.SET_MESSAGES,
          payload: response,
        });

        // Update pagination
        dispatch({
          type: THREAD_ACTIONS.SET_PAGINATION,
          payload: {
            type: "messages",
            pagination: response.pagination,
          },
        });

        return response;
      } catch (error) {
        let errorMessage = "Failed to load messages";

        if (error.type === "AUTH_ERROR") {
          errorMessage = "Authentication required to view messages";
        } else if (error.type === "NOT_FOUND_ERROR") {
          errorMessage = "Messages not found or no longer available";
        } else if (error.type === "NETWORK_ERROR") {
          errorMessage =
            "Connection error - please check your internet connection";
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);

        throw error;
      } finally {
        setLoading("messages", false);
      }
    },
    [
      state.currentThread,
      setLoading,
      clearError,
      setError,
      getAccessTokenSilently,
      initRef,
    ]
  );

  // Update message (for like status, etc.)
  const updateMessage = useCallback((messageId, updates) => {
    dispatch({
      type: THREAD_ACTIONS.UPDATE_MESSAGE,
      payload: {
        _id: messageId,
        ...updates,
      },
    });
  }, []);

  const getCachedThread = useCallback(
    (threadId) => {
      return state.cache.threads.get(threadId) || null;
    },
    [state.cache.threads]
  );

  const clearCache = useCallback(() => {
    dispatch({
      type: THREAD_ACTIONS.SET_CURRENT_THREAD,
      payload: {
        thread: null,
        fight: null,
        event: null,
        metadata: null,
        cache: {
          threads: new Map(),
          lastAccessed: new Map(),
        },
      },
    });
  }, []);

  // Real-time update handlers
  const handleThreadUpdate = useCallback((updateData) => {
    dispatch({
      type: THREAD_ACTIONS.THREAD_UPDATED,
    });

    // Update specific fields based on update type
    if (updateData.messageCount !== undefined) {
      dispatch({
        type: THREAD_ACTIONS.MESSAGE_COUNT_UPDATED,
        payload: updateData.messageCount,
      });
    }

    if (updateData.participantCount !== undefined) {
      dispatch({
        type: THREAD_ACTIONS.PARTICIPANT_COUNT_UPDATED,
        payload: updateData.participantCount,
      });
    }
  }, []);

  // Retry mechanism
  const retryLastOperation = useCallback(async () => {
    if (state.currentThread?.fightId) {
      return await loadFightThread(state.currentThread.fightId);
    }
    throw new Error("No operation to retry");
  }, [state.currentThread, loadFightThread]);

  // Memory management
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    const maxAge = 10 * 60 * 1000; // 10 minutes (increased from 5)
    const maxSize = 50; // Maximum cached threads

    const newThreads = new Map();
    const newLastAccessed = new Map();

    // Sort by last accessed time
    const sortedEntries = Array.from(state.cache.lastAccessed.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, maxSize);

    // Keep only recent and frequently accessed threads
    // NEVER remove the current thread
    sortedEntries.forEach(([threadId, lastAccessed]) => {
      const isCurrentThread = threadId === state.currentThread?.id;
      const isRecent = now - lastAccessed < maxAge;

      if (isCurrentThread || isRecent) {
        const threadData = state.cache.threads.get(threadId);
        if (threadData) {
          newThreads.set(threadId, threadData);
          newLastAccessed.set(threadId, lastAccessed);
        }
      }
    });

    // Always ensure current thread is preserved
    if (state.currentThread?.id && !newThreads.has(state.currentThread.id)) {
      const currentThreadData = state.cache.threads.get(state.currentThread.id);
      if (currentThreadData) {
        newThreads.set(state.currentThread.id, currentThreadData);
        newLastAccessed.set(state.currentThread.id, now);
      }
    }

    if (newThreads.size !== state.cache.threads.size) {
      dispatch({
        type: THREAD_ACTIONS.SET_CURRENT_THREAD,
        payload: {
          ...state,
          cache: {
            threads: newThreads,
            lastAccessed: newLastAccessed,
          },
        },
      });
    }
  }, [state]);

  // Cleanup cache periodically (less frequently)
  React.useEffect(() => {
    const interval = setInterval(cleanupCache, 5 * 60 * 1000); // Every 5 minutes instead of 1
    return () => clearInterval(interval);
  }, [cleanupCache]);

  // Context value
  const value = {
    // State
    ...state,

    // Actions
    setLoading,
    setError,
    clearError,
    setCurrentThread,
    updateThreadMetadata,
    clearCurrentThread,

    // Operations
    loadFightThread,
    postMessage,
    loadMessages,
    updateMessage,
    getCachedThread,
    clearCache,
    retryLastOperation,

    // Real-time
    handleThreadUpdate,

    // Utilities
    cleanupCache,
  };

  return (
    <ThreadContext.Provider value={value}>{children}</ThreadContext.Provider>
  );
};

export default ThreadContext;
